/**
 * `FollowUp` is recorded only by the backend's automatic follow-up: it can be
 * read, never sent. A contact without any history has no status at all.
 */
export type ContactStatus = 'Contacted' | 'Closed' | 'FollowUp';

export const contactStatusLabels: Record<ContactStatus, string> = {
  Contacted: 'Contattato',
  Closed: 'Chiusura',
  FollowUp: 'Follow-up',
};

/**
 * Stato del follow-up automatico: il BE registra da sé gli eventi `FollowUp` e
 * manda le email, fino a `maxAttempts`. È valorizzato solo finché il contatto
 * è in follow-up, quindi il FE lo legge e basta, senza dedurlo dallo storico.
 */
export interface ContactFollowUp {
  attempts: number;
  maxAttempts: number;
  nextFollowUpAt?: Date;
  isExhausted: boolean;
}

export interface Contact {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phoneNumber?: string;
  role?: string;
  contactSourceId?: string;
  contactSourceName?: string;
  contactSourceSubName?: string;
  referrerId?: string;
  referrerFirstName?: string;
  referrerLastName?: string;
  referrerEmail?: string;
  /** Utente del portale collegato al contatto: valorizzati solo se il collegamento esiste. */
  userId?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  currentStatus?: ContactStatus;
  lastContactedAt?: Date;
  /** Quando il contatto ci ha scritto la prima volta; assente se non è noto. */
  receivedAt?: Date;
  /** When the contact unsubscribed from the CRM emails; none is sent to it any more. */
  unsubscribedAt?: Date;
  followUp?: ContactFollowUp;
}

/** Perché il BE propone questo utente per il contatto, dalla più forte alla più debole. */
export type ContactUserMatchReason =
  | 'Email'
  | 'FullName'
  | 'EmailLocalPart'
  | 'EmailDomain';

export interface ContactUserSuggestion {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  matchReasons: ContactUserMatchReason[];
}

export interface LinkContactUserRequest {
  userId: string;
  /** Sostituisce l'email del contatto con quella con cui l'utente si è registrato. */
  replaceEmail: boolean;
}

export interface ContactHistoryEvent {
  id: string;
  status: ContactStatus;
  contactClosureReasonId?: string;
  contactClosureReasonName?: string;
  contactedAt: Date;
}

export interface ContactWithHistory extends Contact {
  contactHistories: ContactHistoryEvent[];
}

export interface CreateContactRequest {
  firstName: string | null;
  lastName: string | null;
  email: string;
  phoneNumber: string | null;
  role: string | null;
  contactSourceId: string | null;
  referrerId: string | null;
}

export interface UpdateContactRequest extends CreateContactRequest {
  id: string;
}

export interface AddContactHistoryRequest {
  status: ContactStatus;
  contactClosureReasonId: string | null;
  contactedAt: Date | null;
}

export interface Referrer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  contactsCount: number;
}

export interface CreateReferrerRequest {
  firstName: string;
  lastName: string;
  email: string;
}

export interface UpdateReferrerRequest extends CreateReferrerRequest {
  id: string;
}

export interface ContactSource {
  id: string;
  name: string;
  /** Detail within the channel, e.g. the "ID Azione" of a website form. Name and sub name are unique together. */
  subName?: string;
  isActive: boolean;
  createdAt: Date;
}

export interface CreateContactSourceRequest {
  name: string;
  subName: string | null;
}

export interface UpdateContactSourceRequest extends CreateContactSourceRequest {
  id: string;
  isActive: boolean;
}

export interface ContactClosureReason {
  id: string;
  name: string;
  isActive: boolean;
  /** Closing a contact with this reason starts the backend's automatic follow-up emails. */
  requiresFollowUp: boolean;
  createdAt: Date;
}

export interface CreateContactClosureReasonRequest {
  name: string;
  requiresFollowUp: boolean;
}

export interface UpdateContactClosureReasonRequest extends CreateContactClosureReasonRequest {
  id: string;
  isActive: boolean;
}

export interface CrmSettings {
  /** Whether the CRM sends anything to the contacts: first-contact email, follow-ups, WhatsApp auto-reply. */
  notificationsEnabled: boolean;
  /** Whether the first-contact email is sent for the WhatsApp messages. */
  whatsAppEmailEnabled: boolean;
  /** Whether the WhatsApp auto-reply is sent (off, it is only logged). */
  whatsAppSendEnabled: boolean;
  /** Testo della risposta automatica su WhatsApp, `{{ name }}` è il nome del contatto; vuoto la disattiva. */
  whatsAppAutoReply?: string;
  /** Giorni dalla chiusura del contatto al primo follow-up. */
  followUpFirstDelayDays: number;
  /** Giorni dal primo follow-up al secondo. */
  followUpSecondDelayDays: number;
  updatedAt?: Date;
}

export interface UpdateCrmSettingsRequest {
  notificationsEnabled: boolean;
  whatsAppEmailEnabled: boolean;
  whatsAppSendEnabled: boolean;
  whatsAppAutoReply?: string;
  followUpFirstDelayDays: number;
  followUpSecondDelayDays: number;
}

export interface UnsubscribeContactResult {
  email: string;
}
